import { ButtonLooks, FontIcons, Placements, StandardAlert, StandardButton, StandardPopout } from '@brandfolder/react';
import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';

import { useFetch, fetchJson } from '@api/ApiHelper';
import { InsightsEventActions, InsightsEventTypes } from '@api/insights/v1/events';
import { ShareManifestDownloadResponse } from '@api/v3/share_manifests';
import { ShareManifestClient, ShareManfiestResourcePath } from '@api/v4/share_manifests/ShareManifestTypes';
import ClipboardCopy from '@components/common/clipboard_copy/main';
import { PrimaryButton, TertiaryButton, TertiaryIconButton } from '@components/library/button';
import { ActionsDropdown, ActionsOpenDirections } from '@components/library/dropdown';
import { ActionDropdownItem } from '@components/library/dropdown_item';
import BulkShareModal from '@components/show_page/bulk_actions/BulkShareModal';
import { getFilteredArray } from '@helpers/arrays';
import { userCreatedShareLink } from '@helpers/share-links';
import { getStandardPopoutLabels } from '@translations';

import classes from './styles/heading-with-actions.module.scss';
import classNames from 'classnames';
import { FilestackPreviewImage } from '@components/common/filestack_uploader/FilestackPreviewImage';

interface HeadingWithActionsProps {
  customLogoUrl?: string | null;
  editable: boolean;
  hasAssets: boolean;
  hasMixedViewOnlyAssets: boolean;
  hasOnlyViewOnlyAssets: boolean;
  hasViewOnlyAssets: boolean;
  isAttachmentGroup: boolean;
  isPreview: boolean;
  name: string;
  readable: boolean;
  resourcePath: string;
  resourcePaths: ShareManfiestResourcePath[] | null;
  selectedAssetKeys: string[];
  selectedViewOnlyAssetKeys: string[];
  shareManifest: ShareManifestClient;
  shareUrl: string;
  galleryView?: boolean;
  subject: string;
  totalAssetFriendly: string;
  totalAssets: number;
  viewOnly: boolean;
}

export const HeadingWithActions: FunctionComponent<HeadingWithActionsProps> = (props) => {
  const {
    customLogoUrl = null,
    editable,
    hasAssets,
    hasMixedViewOnlyAssets,
    hasOnlyViewOnlyAssets,
    hasViewOnlyAssets,
    isAttachmentGroup,
    isPreview,
    name,
    readable,
    resourcePath,
    resourcePaths,
    selectedAssetKeys,
    selectedViewOnlyAssetKeys,
    shareUrl,
    shareManifest,
    galleryView = false,
    subject,
    totalAssetFriendly,
    totalAssets,
    viewOnly
  } = props;

  const { digest, key, shareableType, userKey } = shareManifest;

  const [openEditModal, setOpenEditModal] = useState(new URLSearchParams(window.location.search).get('show_edit_share_mode') === 'true');
  const [showDownloadPopout, setShowDownloadPopout] = useState(false);

  const getAssetKeysToDownload = (): string[] => {
    if (hasViewOnlyAssets) {
      return getFilteredArray(Array.from(selectedAssetKeys), Array.from(selectedViewOnlyAssetKeys));
    } else {
      return Array.from(selectedAssetKeys);
    }
  };

  const postShareManifestDownload = useFetch<ShareManifestDownloadResponse>({
    body: {
      data: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        asset_keys: getAssetKeysToDownload()
      },
    },
    fetchOnMount: false,
    method: 'POST',
    url: `/api/v3/share_manifests/${digest}/assets/download`
  });

  const handleDownload = (): void => {
    postShareManifestDownload.fetch();
  };

  const postAssetDownloadEvent = (assetKey: string): Promise<null> => (
    fetchJson({
      body: {
        data: {
          attributes: {
            data: {
              action_name: InsightsEventActions.DOWNLOADED, // eslint-disable-line @typescript-eslint/naming-convention
              resource_key: assetKey, // eslint-disable-line @typescript-eslint/naming-convention
              resource_type: InsightsEventTypes.ASSET, // eslint-disable-line @typescript-eslint/naming-convention
              source: {
                resource_key: BFG.resource.key, // eslint-disable-line @typescript-eslint/naming-convention
                resource_type: BFG.resource.type, // eslint-disable-line @typescript-eslint/naming-convention
                share_manifest_key: key // eslint-disable-line @typescript-eslint/naming-convention
              }
            }
          }
        }
      },
      method: 'POST',
      url: `${BFG.INSIGHTS_API_URL}/api/v1/events`
    })
  );

  const downloadFile = (href: string): void => {
    const element = document.createElement('a');
    element.setAttribute('download', 'true');
    element.setAttribute('href', href);
    element.style.display = 'none';

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleInsights = (): void => {
    // only create insights downloaded events for non-creators
    if (!userCreatedShareLink(BFG.currentUser?.user_id, userKey) && !BFG.currentUser?.su) {
      // get asset keys through elements on page because we only download the assets the user has
      // access to (for example: no unapproved assets are shown but would be listed in selectedAssetKeys)
      const allAssets = document.getElementsByClassName('asset-card');
      for (let i = 0; i < allAssets.length; i++) { // eslint-disable-line no-plusplus
        const asset = allAssets[i];
        postAssetDownloadEvent(asset.id);
      }
    }
  };

  useEffect(() => {
    if (postShareManifestDownload.error) {
      Notify.create({
        title: bfTranslate('There was an error downloading your assets. Please try again.'),
        type: 'error'
      });
    }
  }, [postShareManifestDownload.error]);

  useEffect(() => {
    if (postShareManifestDownload.response) {
      handleInsights();
      downloadFile(postShareManifestDownload.response.data.redirect_url);
    }
  }, [postShareManifestDownload.response]);

  const handlePrint = (): void => {
    window.print();
  };

  const renderHeading = (): ReactNode => {
    const hasResourcePaths = resourcePaths && resourcePaths.length > 1;
    return (
      <>
        {name}
        {hasResourcePaths && (
          <>
            {' '}{bfTranslate('from')}{' '}
            {resourcePaths.map((rp, index) => (
              <><a href={rp.path}>{rp.name}</a>{index !== resourcePaths.length - 1 ? ', ' : ''}</>
            ))}
          </>
        )}
        {!hasResourcePaths && readable && (
          <>
            {' '}{bfTranslate('from')}{' '}<a href={resourcePath}>{subject}</a>
          </>
        )}
        {viewOnly && (
          <span className={classes.viewOnly} id="share-manifest-view-only">{' '}({bfTranslate('View Only')})</span>
        )}
      </>
    );
  };

  const handleDownloadZip = (): void => {
    setShowDownloadPopout(false);

    if (BFG.downloadAlertEnabled) {
      BF.fx.dispatchWindowEvent('launchDownloadAlert', null, {
        downloadCallback: handleDownload
      });
    } else {
      handleDownload();
    }
  };

  const showDownloadAll = (): boolean => {
    if (hasOnlyViewOnlyAssets) return false;

    return !isAttachmentGroup && !viewOnly && !BFG.disableCollectionAdminDownload;
  };

  const renderDownloadAll = (): ReactNode => {
    if (!showDownloadAll()) return null;

    const downloadAllZipText = hasMixedViewOnlyAssets ? bfTranslate('Download') : `${bfTranslate('Download All')} (${totalAssetFriendly} - ZIP)`;
    const downloadAllText = postShareManifestDownload.loading ? bfTranslate('Downloading...') : downloadAllZipText;

    if (hasMixedViewOnlyAssets) {
      const id = 'download-all-popout';

      return (
        <StandardPopout
          className={classes.popout}
          content={<>
            <StandardAlert icon={FontIcons.Show}>
              {bfTranslate('One or more assets are view only and will not be included in the download.')}
            </StandardAlert>
            <StandardButton
              fullWidth
              look={ButtonLooks.Default}
              onClick={(): void => handleDownloadZip()}
              startIcon={FontIcons.Download}
            >
              {bfTranslate('Download as ZIP')}
            </StandardButton>
          </>}
          id={id}
          labels={getStandardPopoutLabels()}
          open={showDownloadPopout}
          placement={Placements.BottomEnd}
          setOpen={(): void => setShowDownloadPopout(false)}
        >
          <PrimaryButton
            disabled={postShareManifestDownload.loading}
            icon="bff-download"
            id="share-manifest-download"
            onClick={(): void => setShowDownloadPopout(!showDownloadPopout)}
          >
            {downloadAllText}
          </PrimaryButton>
        </StandardPopout>
      );
    }

    return (
      galleryView ? 
        (
        <TertiaryButton
          disabled={postShareManifestDownload.loading}
          icon="bff-download"
          id="share-manifest-download"
          onClick={(): void => handleDownloadZip()}
        >
          {downloadAllText}
        </TertiaryButton>
        ) : 
        (
        <PrimaryButton
          disabled={postShareManifestDownload.loading}
          icon="bff-download"
          id="share-manifest-download"
          onClick={(): void => handleDownloadZip()}
        >
          {downloadAllText}
        </PrimaryButton>
        )
    );
  };

  return (
    <>
      <section className={classNames(classes.section, {
        [classes.galleryViewSection]: galleryView,
      })}>
        {!galleryView && <h1 className={classes.h1} id="share-manifest-heading">{renderHeading()}</h1>}
        {galleryView && customLogoUrl &&  
          <div className={classes.customLogoContainer}>   
            <FilestackPreviewImage
            alt="Header"
            className={classes.customLogoImage}
            data-private
            src={customLogoUrl}
            />
          </div>
        }
        <div className={classes.actions} id="share-manifest-actions">
          {shareableType === 'AssetGroup' && (
            <p
              className={classes.assetCount}
              id="share-manifest-asset-count"
            >
              {totalAssets} {totalAssets === 1 ? bfTranslate('Asset') : bfTranslate('Assets')}
            </p>
          )}
          {!isPreview && (
            <>
              <ActionsDropdown
                anchorElement={(
                  <TertiaryButton
                    icon="bff-ellipsis"
                    id="share-manifest-more-options"
                  >
                    {bfTranslate('More Options')}
                  </TertiaryButton>
                )}
                className={classes.moreOptions}
                id="share-manifest-more-options"
                openDirection={ActionsOpenDirections.DownLeft}
                openOnClick
                openOnHover={false}
              >
                {editable && (
                  <ActionDropdownItem
                    icon="bff-edit"
                    name="edit"
                    onChoose={(): void => setOpenEditModal(true)}
                  >
                    {bfTranslate('Edit Share Link')}
                  </ActionDropdownItem>
                )}
                <ActionDropdownItem
                  icon="bff-link"
                  name="copy"
                >
                  <ClipboardCopy
                    showFeedbackAsTooltip
                    textToCopy={shareUrl}
                  >
                    {bfTranslate('Copy Share Link URL')}
                  </ClipboardCopy>
                </ActionDropdownItem>
              </ActionsDropdown>
              {hasAssets && !isAttachmentGroup && !viewOnly && BFG.hasFeature('contact_sheets') && (
                <TertiaryIconButton
                  aria-label={bfTranslate('Print Contact Sheet')}
                  className={classes.print}
                  icon="bff-print"
                  id="share-manifest-print-contact-sheet"
                  onClick={(): void => handlePrint()}
                  title={bfTranslate('Print Contact Sheet')}
                />
              )}
              {renderDownloadAll()}
            </>
          )}
        </div>
      </section>
      {editable && openEditModal && (
        <BulkShareModal
          closeModal={(): void => setOpenEditModal(false)}
          selectedAssetKeys={new Set(selectedAssetKeys)}
        />
      )}
    </>
  );
};
